import React, { useState, useEffect } from 'react';
import { ModalX } from '../../../components';
import {
    Button,
    Checkbox,
    Input,
    List,
    ListItem,
    Spinner,
    Textarea,
    Tab,
    TabsHeader,
    TabsBody,
    TabPanel,
    Tabs
} from '@material-tailwind/react';
import { ScanQrCodeIcon, SearchIcon, SofaIcon } from 'lucide-react';
import { dateToTime, getOrdinal, NotifyError, timeToDate } from '../../../utils/functions';
import { useGetOffices } from '../../../custom-hooks/use-offices';
import { useGetSeats } from '../../../custom-hooks/use-seats';
import { useAddBooking, useUpdateBooking } from '../../../custom-hooks/use-bookings';
import moment from 'moment';
import { InterpretActiveStatus } from '../../../utils/ui-interpreters';
import { OPERATIONAL_STATUSES } from '../../../utils/enums';

const BookingFormModal = ({
    isOpen,
    onClose,
    // isUpdating,
    updatingBookingId,
    customerId,
    defaultData,
    onSuccess
}) => {

    const isUpdating = !!updatingBookingId;
    const [formData, setFormData] = useState({
        office: null,
        seat: null,
        startDate: '',
        endDate: '',
        dailyStartTime: '',
        dailyEndTime: '',
        rentTotal: 0,
        comment: '',
        ...defaultData
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('');
    const [isFullDayAccess, setIsFullDayAccess] = useState(false);
    const [isOpenEnded, setIsOpenEnded] = useState(false);

    const { data: offices, isLoading: isLoadingOffices, refetch: refetchOffices } = useGetOffices();
    const { data: seats, isLoading: isLoadingSeats, refetch: refetchSeats } = useGetSeats();

    const updateBookingMutation = useUpdateBooking();
    const addBookingMutation = useAddBooking();

    // Set initial data when booking data is loaded
    useEffect(() => {
        if (defaultData) {
            setFormData({
                contract: defaultData.contract || null,
                user: defaultData.user || null,
                office: defaultData.office?._id || null,
                seat: defaultData.seat?._id || null,
                startDate: moment(defaultData.startDate).format('YYYY-MM-DD') || '',
                endDate: moment(defaultData.endDate).format('YYYY-MM-DD') || '',
                dailyStartTime: defaultData.dailyStartTime ? dateToTime(defaultData.dailyStartTime) : '',
                dailyEndTime: defaultData.dailyEndTime ? dateToTime(defaultData.dailyEndTime) : '',
                rentTotal: defaultData.rentTotal || 0,
                comment: defaultData.comment || '',
                status: defaultData.status || '',
            });
            setIsOpenEnded(defaultData.endDate === null);
            setIsFullDayAccess(defaultData.dailyStartTime === null && defaultData.dailyEndTime === null);

            // Set active tab based on what the booking contains
            if (defaultData.office) {
                setActiveTab('office');
            } else if (defaultData.seat) {
                setActiveTab('seat');
            }
        }
    }, [defaultData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTimeChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOfficeSelection = (officeId) => {
        setFormData(prev => ({
            ...prev,
            office: officeId
        }));
    };

    const handleSeatSelection = (seatId) => {
        setFormData(prev => ({
            ...prev,
            seat: seatId
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isUpdating) {
            try {
                const updateData = {
                    user: customerId,
                    contract: formData.contract,
                    startDate: new Date(formData.startDate),
                    endDate: isOpenEnded ? null : new Date(formData.endDate),
                    dailyStartTime: timeToDate(formData.dailyStartTime),
                    dailyEndTime: timeToDate(formData.dailyEndTime),
                    rentTotal: formData.rentTotal,
                    comment: formData.comment,
                    // Don't update these fields as per requirements
                    ...(formData.office && { office: formData.office }),
                    ...(formData.seat && { seat: formData.seat })
                };

                await updateBookingMutation.mutateAsync({
                    bookingId: updatingBookingId,
                    data: updateData
                });
                onSuccess();
                onClose();
            } catch (error) {
                NotifyError(error.response?.data?.message || 'Failed to update booking', error);
                console.error('Error updating booking', error);
            } finally {
                refetchOffices();
                refetchSeats();
            }
        }
        else {
            if (formData.office && formData.seat) {
                NotifyError('Please select either office or seat, not both', 'office and seat both selected');
                return;
            }
            try {
                const newBooking = {
                    user: customerId,
                    contract: formData.contract,
                    ...(formData.office && { office: formData.office }),
                    ...(formData.seat && { seat: formData.seat }),
                    startDate: new Date(formData.startDate),
                    endDate: isOpenEnded ? '' : new Date(formData.endDate),
                    dailyStartTime: isFullDayAccess ? '' : timeToDate(formData.dailyStartTime),
                    dailyEndTime: isFullDayAccess ? '' : timeToDate(formData.dailyEndTime),
                    rentTotal: formData.rentTotal || 0,
                    comment: formData.comment || '',
                }
                await addBookingMutation.mutateAsync(newBooking);
                onSuccess();
                onClose();
            } catch (error) {
                NotifyError(error.response?.data?.message || 'Failed to add booking', error);
                console.error('Error adding booking', error);
            }
        }
    };

    const filteredOffices = offices?.filter(office =>
        office.code.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const filteredSeats = seats?.filter(seat =>
        seat.seatCode.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    return (
        <ModalX
            isOpen={isOpen}
            onClose={onClose}
            title={isUpdating ? "Edit Booking" : "Add Booking"}
            maxWidth="xl"
        >
            <form onSubmit={handleFormSubmit}>
                <div className="space-y-4">
                    {/* Booking Info Display */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Input
                                    name='startDate'
                                    type="date"
                                    label="Start Date"
                                    value={formData.startDate}
                                    onChange={(e) => handleInputChange(e)}
                                // min={moment().format('YYYY-MM-DD')} // TODO: add this functionality by passing the contract start,end dates
                                // max={moment().add(1, 'year').format('YYYY-MM-DD')}
                                />
                            </div>
                            <div>
                                <Checkbox
                                    checked={isOpenEnded}
                                    onChange={(e) => setIsOpenEnded(e.target.checked)}
                                    label={
                                        <>
                                            Open-ended
                                            <p className="text-xs text-gray-600">
                                                If checked, the booking will have no end date
                                            </p>
                                        </>
                                    }
                                    size="sm"
                                />

                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Status</label>
                                <div className="flex">{InterpretActiveStatus(formData.status)}</div>
                            </div>
                            <div className="mt-2">
                                {
                                    !isOpenEnded && (
                                        <Input
                                            name='endDate'
                                            type="date"
                                            label="End Date"
                                            value={formData.endDate}
                                            disabled={isOpenEnded}
                                            onChange={(e) => handleInputChange(e)}
                                        />
                                    )
                                }
                            </div>
                        </div>
                    </div>

                    <Checkbox
                        checked={isFullDayAccess}
                        onChange={(e) => setIsFullDayAccess(e.target.checked)}
                        label="Full day access"
                        size="sm"
                    />
                    {
                        !isFullDayAccess && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    type="time"
                                    label="Daily Start Time"
                                    value={formData.dailyStartTime}
                                    onChange={(e) => handleTimeChange('dailyStartTime', e.target.value)}
                                    disabled={isFullDayAccess}
                                />
                                <Input
                                    type="time"
                                    label="Daily End Time"
                                    value={formData.dailyEndTime}
                                    onChange={(e) => handleTimeChange('dailyEndTime', e.target.value)}
                                    disabled={isFullDayAccess}
                                />
                            </div>
                        )
                    }

                    {/* Tabs for Office/Seat */}
                    <Tabs value={activeTab} className="w-full">
                        <TabsHeader className="rounded-lg bg-gray-100 p-2">
                            {
                                isUpdating ?
                                    (
                                        <>
                                            {
                                                formData?.office && <Tab
                                                    value="office"
                                                    className="flex items-center gap-2"
                                                    onClick={() => setActiveTab('office')}
                                                >
                                                    <ScanQrCodeIcon size={16} />
                                                    Office
                                                </Tab>
                                            }

                                            {
                                                formData?.seat &&
                                                <Tab
                                                    value="seat"
                                                    className="flex items-center gap-2"
                                                    onClick={() => setActiveTab('seat')}
                                                >
                                                    <SofaIcon size={16} />
                                                    Seat
                                                </Tab>
                                            }

                                        </>
                                    ) : (
                                        <>
                                            <Tab
                                                value="office"
                                                className="flex items-center gap-2"
                                                onClick={() => setActiveTab('office')}
                                            >
                                                <ScanQrCodeIcon size={16} />
                                                Office
                                            </Tab>
                                            <Tab
                                                value="seat"
                                                className="flex items-center gap-2"
                                                onClick={() => setActiveTab('seat')}
                                            >
                                                <SofaIcon size={16} />
                                                Seat
                                            </Tab>
                                        </>
                                    )
                            }
                        </TabsHeader>

                        <TabsBody>

                            <TabPanel value="office" className="p-0">
                                <div className="mt-4">
                                    <Input
                                        label="Search Offices"
                                        placeholder="Search by office code"
                                        icon={<SearchIcon className="h-4 w-4" />}
                                        value={searchTerm}
                                        disabled={isLoadingOffices}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <div className="h-[250px] overflow-y-auto mt-4">
                                        {isLoadingOffices ? (
                                            <div className="flex justify-center items-center h-full">
                                                <Spinner className="w-4 h-4" />
                                            </div>
                                        ) : (
                                            <List>
                                                {filteredOffices
                                                    .filter(office => office.status === OPERATIONAL_STATUSES.OPERATIONAL)
                                                    .slice() // Create a copy to avoid mutating the original array
                                                    .map((office) => (
                                                        <ListItem
                                                            key={office._id}
                                                            className="py-1"
                                                            selected={formData.office === office._id}
                                                            onClick={() => handleOfficeSelection(office._id)}
                                                        >
                                                            <div className="flex w-full items-center">
                                                                <Checkbox
                                                                    checked={formData.office === office._id}
                                                                    className="h-4 w-4"
                                                                    ripple={false}
                                                                    readOnly
                                                                />
                                                                <div className="flex flex-col w-full ml-3">
                                                                    <div className="font-medium">
                                                                        Office # {office.number} ({office.code})
                                                                    </div>
                                                                    <div className="text-xs text-gray-600">
                                                                        {office.capacity} Seater | Rate: {office.monthlyRate}/month
                                                                        <br />
                                                                        {getOrdinal(office.floor.number)} Floor, {office.floor.building.name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel value="seat" className="p-0">
                                <div className="mt-4">
                                    <Input
                                        label="Search Seats"
                                        placeholder="Search by seat code"
                                        icon={<SearchIcon className="h-4 w-4" />}
                                        value={searchTerm}
                                        disabled={isLoadingSeats}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <div className="h-[250px] overflow-y-auto mt-4">
                                        {isLoadingSeats ? (
                                            <div className="flex justify-center items-center h-full">
                                                <Spinner className="w-4 h-4" />
                                            </div>
                                        ) : (
                                            <List>
                                                {filteredSeats
                                                    .filter(seat => seat.status === OPERATIONAL_STATUSES.OPERATIONAL)
                                                    .slice() // Create a copy to avoid mutating the original array
                                                    .map((seat) => (
                                                        <ListItem
                                                            key={seat._id}
                                                            className="py-1"
                                                            selected={formData.seat === seat._id}
                                                            onClick={() => handleSeatSelection(seat._id)}
                                                        >
                                                            <div className="flex w-full items-center">
                                                                <Checkbox
                                                                    checked={formData.seat === seat._id}
                                                                    className="h-4 w-4"
                                                                    ripple={false}
                                                                    readOnly
                                                                />
                                                                <div className="flex flex-col w-full ml-3">
                                                                    <div className="font-medium">
                                                                        Seat # {seat.seatCode}
                                                                    </div>
                                                                    <div className="text-xs text-gray-600">
                                                                        Space {seat.coworking.number} ({seat.coworking.code})
                                                                        <br />
                                                                        {seat.seatType} - {seat.deskType} Desk
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                        </TabsBody>
                    </Tabs>


                    {/* Rent Information */}
                    <div>
                        <Input
                            type="number"
                            label={`Total Rent (Rate: ${formData.rentRate || 'Not Set'})`}
                            name="rentTotal"
                            value={formData.rentTotal}
                            onChange={handleInputChange}
                        />
                    </div>

                    {/* Comment */}
                    <Textarea
                        label="Comments"
                        name="comment"
                        value={formData.comment}
                        onChange={handleInputChange}
                    />

                    {/* Submit Button */}
                    <div className="flex justify-end gap-4">
                        <Button
                            variant="text"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="rounded-full"
                            disabled={updateBookingMutation.isLoading || addBookingMutation.isLoading}
                        >
                            {updateBookingMutation.isLoading || addBookingMutation.isLoading ? (
                                <div className="flex items-center gap-2">
                                    <Spinner className="w-4 h-4" />
                                    Updating...
                                </div>
                            ) : (
                                isUpdating ? 'Update Booking' : 'Add Booking'
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </ModalX>
    );
};

export default BookingFormModal;